import {
  STOCK_MOVEMENT_TYPE_LABELS,
  STOCK_MOVEMENT_TYPES,
  type StockMovement,
  stockMovementTypeSchema,
} from "@station/contracts";
import { addDays, type LocalDate, toIstanbulDate } from "@station/core";
import {
  Alert,
  Button,
  Card,
  DataTable,
  type DataTableColumn,
  DateRangePicker,
  EmptyState,
  FormField,
  MoneyText,
  Select,
  SkeletonRows,
} from "@station/ui";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { ArrowLeftRight, X } from "lucide-react";
import { type ReactElement, useId, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { ProductCombobox } from "../../../features/invoicing/inputs.js";
import {
  formatSignedQuantity,
  formatStockQuantity,
  moneyExport,
  movementSourceKey,
  quantityExport,
} from "../../../features/market/market-values.js";
import {
  type MovementFilters,
  movementsQuery,
  useProductSummary,
  useStockMovements,
} from "../../../features/market/queries.js";
import {
  MarketPage,
  marketSearchShape,
  MarketStationField,
  NoStations,
  SourceDocLink,
  useMarketStations,
} from "../../../features/market/shared.js";
import { compactSearch } from "../../../features/shifts/shift-values.js";
import { api } from "../../../lib/api.js";
import { useCursorPages } from "../../../lib/cursor-pages.js";
import { errorMessage } from "../../../lib/errors.js";
import { exportRowsToExcel, fetchAllPages } from "../../../lib/excel.js";
import { formatDate, formatDateTime, formatUnitPrice } from "../../../lib/format.js";

const localDateText = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/)
  .optional()
  .catch(undefined);

const searchSchema = z.object({
  ...marketSearchShape,
  product: z.string().uuid().optional().catch(undefined),
  type: stockMovementTypeSchema.optional().catch(undefined),
  from: localDateText,
  to: localDateText,
});

export const Route = createFileRoute("/_app/market/hareketler")({
  validateSearch: (search) => searchSchema.parse(search),
  component: MovementsRoute,
});

/**
 * Stok hareketleri (Prompt 16 — web): append-only defter. Her satır kendinden sonraki miktarı ve değeri taşır;
 * kaynak belgeye (kasa fişi, transfer, fire, sayım) bağlantı verir. Hareket GİRİLMEZ: belgeler yazar.
 * Varsayılan aralık son 30 iş günüdür; ürün kartından gelen bağlantı ürünü süzer.
 */
function MovementsRoute(): ReactElement {
  const { t } = useTranslation();
  return (
    <MarketPage
      title={t("menu.marketMovements")}
      description={t("market.movements.description")}
      breadcrumb={[{ label: t("menu.marketMovements") }]}
    >
      {({ companyId }) => <MovementsBody companyId={companyId} />}
    </MarketPage>
  );
}

function defaultRange(): { from: LocalDate; to: LocalDate } {
  const today = toIstanbulDate(new Date());
  return { from: addDays(today, -29), to: today };
}

function MovementsBody({ companyId }: { companyId: string }): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const search = Route.useSearch();
  const station = useMarketStations(companyId);
  const product = useProductSummary(search.product);
  const productFieldId = useId();
  const fallback = defaultRange();
  const range = { from: search.from ?? fallback.from, to: search.to ?? fallback.to };

  const setSearch = (patch: Record<string, unknown>) =>
    void navigate({
      to: ".",
      search: ((previous: Record<string, unknown>) => compactSearch({ ...previous, ...patch })) as never,
      replace: true,
    });

  const filters: MovementFilters = {
    stationId: station.stationId ?? undefined,
    productId: search.product,
    movementType: search.type,
    businessDateFrom: range.from,
    businessDateTo: range.to,
  };
  const pages = useCursorPages(JSON.stringify(filters));
  const list = useStockMovements(companyId, filters, pages.cursor);

  const columns = useMemo<DataTableColumn<StockMovement>[]>(
    () => [
      {
        id: "occurredAt",
        header: t("market.fields.occurredAt"),
        hideable: false,
        exportValue: (row) => row.occurredAt,
        cell: (row) => (
          <span className="grid gap-0.5">
            <span className="tabular whitespace-nowrap">{formatDateTime(row.occurredAt)}</span>
            <span className="text-xs text-muted-foreground">
              {t("market.movements.businessDate", { date: formatDate(row.businessDate) })}
            </span>
          </span>
        ),
      },
      {
        id: "product",
        header: t("market.fields.product"),
        hideable: false,
        exportValue: (row) => `${row.productCode} ${row.productName}`,
        cell: (row) => (
          <Link
            to="/market/stok/$productId"
            params={{ productId: row.productId }}
            search={{ station: row.stationId }}
            className="grid gap-0.5 underline underline-offset-4"
          >
            <span>{row.productName}</span>
            <span className="font-mono text-xs text-muted-foreground">{row.productCode}</span>
          </Link>
        ),
      },
      {
        id: "warehouse",
        header: t("market.fields.warehouse"),
        exportValue: (row) => row.warehouseCode,
        cell: (row) => <span className="font-mono text-xs">{row.warehouseCode}</span>,
      },
      {
        id: "type",
        header: t("market.fields.movementType"),
        exportValue: (row) => STOCK_MOVEMENT_TYPE_LABELS[row.movementType],
        cell: (row) => STOCK_MOVEMENT_TYPE_LABELS[row.movementType],
      },
      {
        id: "lot",
        header: t("market.fields.lotNo"),
        exportValue: (row) => row.lotNo,
        cell: (row) =>
          row.lotId === null ? (
            <span className="text-muted-foreground">—</span>
          ) : (
            <span className="grid gap-0.5">
              <span className="font-mono text-xs">{row.lotNo ?? t("market.lots.noLotNo")}</span>
              <span className="text-xs text-muted-foreground">{formatDate(row.expiryDate)}</span>
            </span>
          ),
      },
      {
        id: "quantity",
        header: t("market.fields.quantity"),
        align: "right",
        exportValue: (row) => quantityExport(row.quantity),
        cell: (row) => (
          <span className="tabular whitespace-nowrap">{formatSignedQuantity(row.quantity, row.unit)}</span>
        ),
      },
      {
        id: "unitCost",
        header: t("market.fields.unitCost"),
        align: "right",
        defaultVisible: false,
        exportValue: (row) => row.unitCost,
        cell: (row) => <span className="tabular whitespace-nowrap">{formatUnitPrice(row.unitCost)}</span>,
      },
      {
        id: "value",
        header: t("market.fields.value"),
        align: "right",
        exportValue: (row) => moneyExport(row.value),
        cell: (row) => <MoneyText value={row.value} colorize />,
      },
      {
        id: "quantityAfter",
        header: t("market.fields.quantityAfter"),
        align: "right",
        exportValue: (row) => quantityExport(row.quantityAfter),
        cell: (row) => (
          <span className="tabular whitespace-nowrap">
            {formatStockQuantity(row.quantityAfter, row.unit)}
          </span>
        ),
      },
      {
        id: "valueAfter",
        header: t("market.fields.valueAfter"),
        align: "right",
        defaultVisible: false,
        exportValue: (row) => moneyExport(row.valueAfter),
        cell: (row) => <MoneyText value={row.valueAfter} colorize />,
      },
      {
        id: "source",
        header: t("market.fields.source"),
        exportValue: (row) => t(`market.movements.source.${movementSourceKey(row.sourceDocType)}`),
        cell: (row) => (
          <SourceDocLink
            sourceDocType={row.sourceDocType}
            sourceDocId={row.sourceDocId}
            label={t(`market.movements.source.${movementSourceKey(row.sourceDocType)}`)}
          />
        ),
      },
    ],
    [t],
  );

  if (station.error) return <Alert tone="danger">{errorMessage(station.error)}</Alert>;
  if (station.isLoading) return <SkeletonRows rows={4} />;
  if (station.stations.length === 0) return <NoStations />;

  return (
    <>
      <Card className="flex flex-wrap items-end gap-3 p-4">
        <MarketStationField selection={station} />
        <DateRangePicker
          label={t("market.movements.businessDateRange")}
          value={{ from: range.from as LocalDate, to: range.to as LocalDate }}
          onValueChange={(next) => setSearch({ from: next.from ?? undefined, to: next.to ?? undefined })}
        />
      </Card>

      <DataTable
        caption={t("market.movements.caption")}
        columns={columns}
        rows={list.data?.items ?? []}
        rowKey={(row) => row.id}
        loading={list.isPending}
        error={list.error ? <Alert tone="danger">{errorMessage(list.error)}</Alert> : undefined}
        emptyState={
          <EmptyState
            headingLevel={2}
            icon={<ArrowLeftRight />}
            title={t("market.movements.emptyTitle")}
            description={t("market.movements.emptyDescription")}
          />
        }
        filters={
          <>
            <div className="grid w-72 gap-1.5">
              <label htmlFor={productFieldId} className="text-sm font-medium">
                {t("market.fields.product")}
              </label>
              <div className="flex items-center gap-1">
                <ProductCombobox
                  id={productFieldId}
                  className="flex-1"
                  label={product.data && search.product ? `${product.data.code} ${product.data.name}` : ""}
                  onChange={(option) => setSearch({ product: option?.id })}
                />
                {search.product ? (
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label={t("market.movements.clearProduct")}
                    onClick={() => setSearch({ product: undefined })}
                  >
                    <X aria-hidden="true" />
                  </Button>
                ) : null}
              </div>
            </div>
            <FormField label={t("market.fields.movementType")} className="w-52">
              {(control) => (
                <Select
                  {...control}
                  value={search.type ?? ""}
                  onChange={(event) => setSearch({ type: event.target.value || undefined })}
                >
                  <option value="">{t("common.all")}</option>
                  {STOCK_MOVEMENT_TYPES.map((type) => (
                    <option key={type} value={type}>
                      {STOCK_MOVEMENT_TYPE_LABELS[type]}
                    </option>
                  ))}
                </Select>
              )}
            </FormField>
          </>
        }
        pagination={{
          hasPrevious: pages.hasPrevious,
          hasNext: list.data?.page.hasMore ?? false,
          onPrevious: pages.previous,
          onNext: () => pages.next(list.data?.page.nextCursor),
          pageLabel: t("common.pageNumber", { page: pages.pageNumber }),
        }}
        onExport={async (visible) => {
          const rows = await fetchAllPages<StockMovement>(
            async (cursor) =>
              (
                await api.GET("/market/stock/movements", {
                  params: { query: movementsQuery(companyId, filters, cursor, 200) as never },
                })
              ).data!,
          );
          await exportRowsToExcel({
            fileName: "stok-hareketleri",
            sheetName: t("menu.marketMovements"),
            columns: visible,
            rows,
          });
        }}
      />
    </>
  );
}
